import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  GraduationCap, 
  Briefcase,
  UserCheck,
  UserX,
  Clock,
  TrendingUp,
  Award,
  Plus,
  Eye,
  Edit3,
  Star,
  Coffee,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardRH = () => {
  const [stats, setStats] = useState(null);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/dashboard/rh`),
        axios.get(`${API}/users`)
      ]);

      setStats(statsResponse.data);
      setEmployes(usersResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard RH:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrateur',
      commercial: 'Commercial',
      technicien: 'Technicien',
      comptable: 'Comptable',
      depot: 'Dépôt',
      manager_vente: 'Manager Vente',
      manager_logistique: 'Manager Logistique',
      manager_rh: 'Manager RH',
      employe_vente: 'Employé Vente',
      employe_logistique: 'Employé Logistique',
      employe_rh: 'Employé RH'
    };
    return roles[role] || role;
  };

  const getModuleColor = (module) => {
    const colors = {
      vente: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
      logistique: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
      rh: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' }
    };
    return colors[module] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            Dashboard RH
          </h1>
          <p className="text-gray-600">
            Gestion des ressources humaines et développement des équipes
          </p>
        </div>
        <div className="mt-4 lg:mt-0 flex gap-3">
          <button className="btn btn-secondary">
            <Calendar className="w-4 h-4" />
            Planning congés
          </button>
          <button className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Nouveau recrutement
          </button>
        </div>
      </div>

      {/* Stats Cards RH */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card slide-up bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-purple-600">{stats?.total_employes || 0}</div>
              <div className="stats-label text-purple-800">Total Employés</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-green-600">{stats?.employes_actifs || 0}</div>
              <div className="stats-label text-green-800">Actifs</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-orange-50 to-red-50 border-orange-200" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-orange-600">{stats?.conges_en_cours || 0}</div>
              <div className="stats-label text-orange-800">En congés</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <Coffee className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-blue-600">{stats?.formations_planifiees || 0}</div>
              <div className="stats-label text-blue-800">Formations</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance RH & Satisfaction */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Performance RH</h2>
              </div>
              <span className="text-sm text-gray-500">Ce trimestre</span>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Taux de Satisfaction</span>
                  <span className="text-lg font-bold text-success">{stats?.taux_satisfaction?.toFixed(1) || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-success to-green-600 h-3 rounded-full transition-all duration-1000" 
                    style={{ width: `${Math.min(stats?.taux_satisfaction || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">{stats?.employes_actifs || 0}</div>
                  <div className="text-xs text-gray-600">Présents</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-orange-600">{stats?.conges_en_cours || 0}</div>
                  <div className="text-xs text-gray-600">Absents</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{stats?.recrutements_ouverts || 0}</div>
                  <div className="text-xs text-gray-600">Recrutements</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-yellow-500" />
              <h2 className="text-lg font-semibold text-gray-900">Développement</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-blue-900">Formations Techniques</div>
                    <div className="text-sm text-blue-700">{stats?.formations_planifiees || 0} planifiées</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-blue-600">Ce mois</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-green-900">Certifications</div>
                    <div className="text-sm text-green-700">2 en cours</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-green-600">QualiPV</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-purple-900">Évaluations</div>
                    <div className="text-sm text-purple-700">Trimestrielles</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-purple-600">En cours</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Répartition par Module */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Répartition par Module</h2>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Module Vente */}
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-blue-600 mb-2">
                {employes.filter(e => e.module === 'vente').length}
              </div>
              <div className="text-sm font-medium text-blue-800 mb-2">Équipe Vente</div>
              <div className="text-xs text-blue-600">
                Commerciaux, managers vente
              </div>
            </div>

            {/* Module Logistique */}
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-green-600 mb-2">
                {employes.filter(e => e.module === 'logistique').length}
              </div>
              <div className="text-sm font-medium text-green-800 mb-2">Équipe Logistique</div>
              <div className="text-xs text-green-600">
                Techniciens, dépôt, planning
              </div>
            </div>

            {/* Module RH */}
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-purple-600 mb-2">
                {employes.filter(e => e.module === 'rh').length}
              </div>
              <div className="text-sm font-medium text-purple-800 mb-2">Équipe RH</div>
              <div className="text-xs text-purple-600">
                Administration, comptabilité
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des Employés */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserCheck className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Employés Récents</h2>
            </div>
            <button className="text-sm text-primary hover:text-primary-dark font-medium">
              Voir tous les employés
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {employes.length > 0 ? (
            <div className="space-y-0">
              {employes.slice(0, 5).map((employe, index) => {
                const moduleColors = getModuleColor(employe.module);
                
                return (
                  <div key={employe.id} className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {employe.prenom} {employe.nom}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-4">
                            <span>{getRoleLabel(employe.role)}</span>
                            {employe.module && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${moduleColors.bg} ${moduleColors.text} ${moduleColors.border} border`}>
                                Module {employe.module}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`flex items-center gap-1 text-sm ${employe.actif ? 'text-green-600' : 'text-red-600'}`}>
                            {employe.actif ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                            {employe.actif ? 'Actif' : 'Inactif'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Depuis {new Date(employe.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="btn btn-secondary btn-sm">
                            <Eye className="w-3 h-3" />
                          </button>
                          <button className="btn btn-primary btn-sm">
                            <Edit3 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun employé trouvé</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions RH Rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Congés & Absences</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-900">Marie Bernard - Congés payés</span>
                </div>
                <span className="text-xs text-blue-700">15-22 Dec</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-sm font-medium text-orange-900">Pierre Martin - Formation</span>
                </div>
                <span className="text-xs text-orange-700">18-19 Dec</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold text-gray-900">Recrutements</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-900">Technicien Installations</span>
                </div>
                <span className="text-xs text-green-700">3 candidats</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-sm font-medium text-purple-900">Commercial Senior</span>
                </div>
                <span className="text-xs text-purple-700">En cours</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardRH;